import { useState, useMemo } from 'react';
import { ModalWithForm } from '../modal';
import { FormBuilder } from '@/components/formBuilder';
import { useAddCandidateMutation } from '@/store/query/mco-candidates';
import { useShowToast } from '@/hooks';
import { AlreadyPartOf } from './components/';
import { resolveSelectedContactIds } from './resolveSelectedContactIds';
import { useDisclosure, Box } from '@chakra-ui/react';
import { MoveFlowErrors } from '@/components/shared/move-flow-error/MoveFlowError';
import { usePreviewCandidateMutation } from '@/store/query/contacts';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/http';
import { useBulkOperation } from '@/components/ats/BulkOperationQueue';

export const AddToFlow = ({
  modalActions,
  modalClose = () => {},
  contactId,
  isBulk = false,
  onSuccess = () => {},
  alreadyPartOfProps = {},
  bulkOpMethods = null,
  selected = [], // Selected contacts with full data for validation
  // Content-only mode (e.g. job candidates bulk action): render form + validation only, no modal
  asContentOnly = false,
  formRef: externalFormRef = null,
  dispatch = null,
  submitConfig = null,
  initialValuesConfig = null,
  toast: externalToast = null,
}) => {
  const toastFromHook = useShowToast();
  const toast = externalToast ?? toastFromHook;

  // Bulk operation tracking
  const internalBulkOp = useBulkOperation({
    activityType: 'add_to_flow',
  });

  // Use external bulkOpMethods if provided, otherwise use internal
  const { startTracking, completeSuccess, completeWithErrors, completeFailed } =
    bulkOpMethods || internalBulkOp;

  // Get contact IDs for the action. `selected` may be raw contacts, applications
  // with a nested contact, or react-table rows ({ original }) — resolveSelectedContactIds
  // handles all shapes. See its doc.
  const targetIds = useMemo(() => {
    if (asContentOnly || (isBulk && selected?.length > 0)) {
      return resolveSelectedContactIds(selected);
    }
    return Array.isArray(contactId) ? contactId : [contactId].filter(Boolean);
  }, [asContentOnly, isBulk, selected, contactId]);

  const { data: flowOptions = [], isLoading: isFlowOptionsLoading } = useQuery({
    queryKey: ['addable_outreaches', contactId],
    queryFn: () => http.get(`/contacts/${contactId}/addable_outreaches`),
    enabled: !isBulk && !!contactId,
  });

  const { data: bulkFlowOptions = [], isLoading: isBulkFlowOptionsLoading } =
    useQuery({
      queryKey: ['outreaches_list'],
      queryFn: () => http.get('/outreaches/list'),
      enabled: isBulk || asContentOnly,
    });

  const [addCandidate, { isLoading }] = useAddCandidateMutation();
  const [previewCandidate, { isLoading: isPreviewLoading }] =
    usePreviewCandidateMutation();
  const [errors, setErrors] = useState([]);
  const [selectedOutreachId, setSelectedOutreachId] = useState(null);

  // MoveFlowErrors modal - only for single contact actions, not bulk
  const {
    isOpen: isErrorOpen,
    onClose: onErrorClose,
    onOpen: onErrorOpen,
  } = useDisclosure();

  const formConfig = useMemo(
    () => [
      {
        name: 'flow_id',
        label: 'Flow',
        placeholder: 'Select a Flow',
        type: 'select',
        isRequired: true,
        isLoading: isBulk ? isBulkFlowOptionsLoading : isFlowOptionsLoading,
        options: isBulk
          ? (bulkFlowOptions as any)?.data || []
          : (flowOptions as any)?.data || [],
        valueKey: 'id',
        labelKey: 'title',
      },
    ],
    [
      flowOptions,
      bulkFlowOptions,
      isBulk,
      isFlowOptionsLoading,
      isBulkFlowOptionsLoading,
    ]
  );

  // Form config for content-only mode (job candidates bulk action)
  const contentOnlyFormConfig = useMemo(
    () =>
      asContentOnly
        ? [
            {
              name: 'outreachId',
              label: 'Select flow',
              type: 'select',
              isRequired: true,
              isAppendToBody: true,
              fetchConfig: {
                endPoint: '/outreaches/list',
                processResponse: (res) => res?.data,
                httpType: 'v1',
              },
              valueKey: 'id',
              labelKey: 'title',
              containerStyles: {},
            },
          ]
        : [],
    [asContentOnly]
  );

  // Content-only mode: render form + validation for job candidates (no modal)
  if (asContentOnly) {
    const handleContentOnlySubmit = (values) => {
      const handler = submitConfig?.addToFlow || (() => {});
      handler(values, dispatch, toast, bulkOpMethods);
    };

    return (
      <Box>
        <FormBuilder
          initialValues={initialValuesConfig?.({ type: 'addToFlow' }) || {}}
          config={contentOnlyFormConfig}
          onSubmit={handleContentOnlySubmit}
          formRef={externalFormRef}
        />
      </Box>
    );
  }

  // Preview candidate - only for single contact, not bulk (bulk uses validation banner instead)
  const onPreviewCandidate = async (flowId) => {
    const response = await previewCandidate({
      ids: [contactId],
      outreachId: flowId,
    });
    setSelectedOutreachId(flowId);
    const { invalid_candidates, previously_unsubscribed_candidates } =
      (response as any)?.data || {};
    if (
      invalid_candidates?.length ||
      previously_unsubscribed_candidates?.length
    ) {
      setErrors((response as any)?.data);
      onErrorOpen();
      return true;
    } else {
      return false;
    }
  };

  const handleSubmit = async (values) => {
    const { flow_id } = values;

    // For single contact, show preview modal if there are issues
    if (!isBulk) {
      const isToPreview = await onPreviewCandidate(flow_id);
      if (isToPreview) return;
    }

    // Use all selected contacts (validation removed - server will handle duplicates)
    const contactsToAdd = isBulk
      ? targetIds
      : Array.isArray(contactId)
        ? contactId
        : [contactId];
    const totalContacts = contactsToAdd.length;

    // Start tracking for bulk operations
    if (isBulk && startTracking) {
      startTracking(totalContacts);
      modalClose(); // Close modal immediately
    }

    const payload = {
      outreachId: flow_id,
      contacts: contactsToAdd,
      ...(isBulk && { is_bulk_activity: true }),
    };

    try {
      const result: any = await addCandidate(payload);
      // Handle both 'error' and 'errors' keys (API returns 'errors' for add_to_flow)
      const errorList = result?.error || result?.errors || [];

      if (errorList?.length > 0) {
        if (isBulk) {
          // For add_to_flow, errors like "already present" should still show as success
          // because the operation completed - we still show the modal with the results
          if (completeWithErrors) {
            completeWithErrors({
              successCount: totalContacts - errorList.length,
              failedCount: errorList.length,
              errors: errorList.map((e) => ({
                id: e.id || e.contact_id || 'unknown',
                message: e.message || 'Failed to add to flow',
              })),
            });
          } else if (completeSuccess) {
            // Fallback: still show success even with "already present" errors
            completeSuccess(totalContacts);
          }
        } else {
          toast({
            title: 'Contact addition to flow failed',
            description: errorList[0]?.message,
            status: 'error',
          });
        }
        onSuccess();
      } else {
        if (isBulk) {
          // Complete successfully in BulkOperationQueue
          if (completeSuccess) {
            completeSuccess(totalContacts);
          }
        } else {
          toast({ title: 'Contact added to flow.' });
          modalClose();
        }
        onSuccess();
      }
    } catch {
      if (isBulk && completeFailed) {
        completeFailed('Failed to add contacts to flow');
      } else {
        toast({
          title: 'Contact addition to flow failed',
          status: 'error',
        });
      }
    }
  };

  return (
    <>
      <ModalWithForm
        isOpen={modalActions.isOpen}
        onClose={modalClose}
        formConfig={formConfig}
        primaryCtaText="Add to Flow"
        isCtaLoading={isLoading || (!isBulk && isPreviewLoading)}
        headerText="Add to Flow"
        subHeaderText={`Select which flow you want to add this candidate${
          isBulk ? 's' : ''
        } to`}
        handleSubmit={handleSubmit}
        showAdditionalBlockAfter={true}
        AdditionalBlockAfter={AlreadyPartOf}
        additionalBlockAfterProps={alreadyPartOfProps}
      />
      {/* MoveFlowErrors modal - only for single contact actions */}
      {!isBulk && isErrorOpen && (
        <MoveFlowErrors
          isOpen={isErrorOpen}
          onClose={onErrorClose}
          onCloseParent={modalClose}
          errors={errors}
          targetOutreachId={selectedOutreachId}
          allCandidateIds={[contactId]}
        />
      )}
    </>
  );
};
